import React, { useRef } from 'react'

import { useFlag } from '@unleash/proxy-client-react'
import { Box, Flex, Icon, Text } from 'stardust'

import colors from '~/ui-components/tokens/colors'
import ToolTip from '~/ui-components/ToolTip'

import { createTypography } from '~/utils'

import { aiResponsePrefix, userMessagePrefix } from '../../Form/AssistModal/constants/assistPrefixes'
import Conversation from '../../Form/AssistModal/Conversation/Conversation'
import { AssistActions } from '../../Form/AssistModal/interface'

interface Props {
  aiAssistantLogs?: Playground.AiAssistantResponseLog[]
  tagType: string
}

const MAX_CONVERSATION_HEIGHT = '464px'
const MIN_CONVERSATION_HEIGHT = '0px'

const AiAssistLog = ({ tagType, aiAssistantLogs }: Props) => {
  const generateLearningV2Flag = useFlag('edu-playground-learning-documentation')
  const infoRef = useRef(null)
  const [showInfo, setShowInfo] = React.useState(false)

  const hideInfoTooltip = () => setShowInfo(false)
  const showInfoTooltip = () => setShowInfo(true)

  return tagType === 'Assist Log' ? (
    <Box p={3} bg={colors.cosmicShade12} borderRadius="16px" mt={3} data-qa="ai-assist-log">
      <Flex alignItems="center" mb={3}>
        <Text {...createTypography('large', 'bold')} mr={2}>
          Assistant Log (Private)
        </Text>
        <Box
          ref={infoRef}
          data-qa="ai-assist-log-info-icon"
          tabIndex={0}
          role="button"
          aria-label="Show assistant log information"
          onClick={showInfoTooltip}
          onFocus={showInfoTooltip}
          onBlur={hideInfoTooltip}
          onMouseEnter={showInfoTooltip}
          onMouseLeave={hideInfoTooltip}>
          <Icon name="info" width={20} color={colors.cosmicShade10} />
          <ToolTip anchor={infoRef} placement="bottom" show={showInfo} onClickedOutside={hideInfoTooltip}>
            <Box px={3} py="12px">
              <Text {...createTypography('caption')} color="white">
                This log shows all the information that was input and output into the Assistant. This
                information is private and is not visible to families.
              </Text>
            </Box>
          </ToolTip>
        </Box>
      </Flex>

      {/* assist log  */}
      {aiAssistantLogs?.length ? (
        <Conversation
          messages={convertLogsToMessages(aiAssistantLogs, generateLearningV2Flag)}
          maxHeight={MAX_CONVERSATION_HEIGHT}
          minHeight={MIN_CONVERSATION_HEIGHT}
          isLog={true}
        />
      ) : (
        <Text {...createTypography('small')} color={colors.cosmicShade10} mt={2}>
          No interactions with the AI assistant yet.
        </Text>
      )}
    </Box>
  ) : null
}

AiAssistLog.displayName = 'AiAssistLog'

export default React.memo(AiAssistLog)

const convertLogsToMessages = (
  messageLog: Playground.AiAssistantResponseLog[],
  v2Flag: boolean
): Playground.AssistantConversationMessage[] => {
  const messages: Playground.AssistantConversationMessage[] = []

  messageLog.forEach((message, index) => {
    const userPrompt = message.userPrompt.content
    const aiResponse = message.aiResponse
    const timestamp = message.updatedAt
    const action = message.userPrompt.action.toLocaleUpperCase() as Playground.AssistActions
    const isTryAgain = message.userPrompt.is_try_again
    const generateLearningV2 = v2Flag && action === AssistActions.GENERATE

    // Create USER message
    const userMessage: Playground.AssistantConversationMessage = {
      id: (index * 2 + 1).toString(),
      sender: 'USER',
      content: isTryAgain
        ? {
            boldText: 'Try Again',
            regularText: '',
          }
        : {
            boldText: userMessagePrefix[action] || '',
            regularText: userPrompt,
          },
      timestamp: timestamp,
      withTextReveal: false,
    }

    // Create AI message
    const aiMessage: Playground.AssistantConversationMessage = {
      id: (index * 2 + 2).toString(),
      sender: 'AI',
      content: {
        boldText: (generateLearningV2 ? '📚 ' : '') + aiResponsePrefix[action] || '',
        regularText: aiResponse,
      },
      timestamp: timestamp,
      withTextReveal: false,
    }

    messages.push(userMessage)
    messages.push(aiMessage)
  })

  return messages
}
